// src/auth/strategies/DappAuthManager.tsx
import { useAccounts } from '@hooks/dapp/useAccounts';
import { useConnect } from '@hooks/dapp/useConnect';
import { useSnackbar } from 'notistack';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { LoggedInUser, UserContext } from './UserContext';

// Sometimes we get the same user twice+, but because the instance is different
// we propagate the change via useState. This check ensures we only rerender when the user actually changed.
const compareWithPrevious = (nextUser: LoggedInUser | undefined) => {
  return (currentUser: LoggedInUser | undefined) => {
    if (
      currentUser?.userId === nextUser?.userId &&
      currentUser?.primaryParty === nextUser?.primaryParty &&
      currentUser?.userAccessToken === nextUser?.userAccessToken
    ) {
      return currentUser;
    }

    return nextUser;
  };
};

export const DappAuthManager: React.FC<PropsWithChildren> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { disconnect, status, isLoading, connect } = useConnect();
  const isConnected = !!status?.connection?.isConnected;
  const { isLoading: accountsLoading, primaryAccount } = useAccounts(isConnected);
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>();
  const loadingUserAccounts = !!isConnected && accountsLoading;

  useEffect(() => {
    if (isLoading) return;

    if (isConnected && status?.session) {
      setLoggedInUser(
        compareWithPrevious({
          userId: status.session.userId,
          userAccessToken: status.session.accessToken,
          primaryParty: primaryAccount?.partyId || '',
          isConnectedViaDapp: true,
        }),
      );
    }
    if (status === undefined) {
      setLoggedInUser(compareWithPrevious(undefined));
    }
  }, [isConnected, isLoading, status, primaryAccount, enqueueSnackbar, accountsLoading]);
  const login = React.useCallback(async () => {
    try {
      await connect();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Error connecting', { variant: 'error' });
    }
  }, [enqueueSnackbar, connect]);
  const contextValue = useMemo(
    () => ({
      loggedInUser,
      isLoading: loadingUserAccounts,
      login,
      isConnecting: isLoading,
      logout: async () => {
        await disconnect();
        setLoggedInUser(compareWithPrevious(undefined));
      },
      isSelfSignedToken: () => false,
      signoutOnUnauthorized: () => {},
    }),
    [loggedInUser, disconnect, login, loadingUserAccounts, isLoading],
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
